import React, { useState } from 'react';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Table, Button, Text } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { HRS_CONFIG } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { useHrsLocationsQuery } from '@admin/schema';

import { HRSLocationType, UpsertModal } from './upsert_modal';

export const HRSLocationsList: React.FC = () => {
  useTitle('HRS Locations');
  const [showUpsertModal, setShowUpsertModal] = useState<boolean>(false);
  const [editingLocation, setEditingLocation] = useState<HRSLocationType | undefined>(undefined);
  const canView = usePermission(HRS_CONFIG.resource, HRS_CONFIG.actions.read);
  const canUpdate = usePermission(HRS_CONFIG.resource, HRS_CONFIG.actions.update);

  const { data, loading, refetch } = useHrsLocationsQuery({
    client,
    skip: !canView,
  });

  return (
    <Permission
      resource={HRS_CONFIG.resource}
      action={HRS_CONFIG.actions.read}
      denied={
        <Panel>
          <Panel.Body>
            <Text tag="p" style="muted">
              You do not have permission to view HRS configuration.
            </Text>
          </Panel.Body>
        </Panel>
      }
    >
      <Panel>
        <Panel.Body>
          <Table responsive striped>
            <thead>
              <tr>
                <th className="col-sm-2">Code</th>
                <th className="col-sm-2">Region</th>
                {canUpdate && (
                  <th className="col-sm-1 text-right">
                    <Button
                      type="button"
                      kind="mint"
                      onClick={() => {
                        setEditingLocation(undefined);
                        setShowUpsertModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add
                    </Button>
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {data?.hrsLocations.map((location) => (
                <tr key={location.id}>
                  <td className="col-sm-2">{location.code}</td>
                  <td className="col-sm-2">{location.region?.name}</td>
                  {canUpdate && (
                    <td className="col-sm-1 text-right">
                      <Button
                        kind="primary"
                        onClick={() => {
                          setEditingLocation(location);
                          setShowUpsertModal(true);
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          {loading && <Spinner />}
        </Panel.Body>
      </Panel>
      {showUpsertModal && canUpdate && (
        <UpsertModal
          location={editingLocation}
          regionOptions={data?.regions ?? []}
          closeModal={() => {
            setEditingLocation(undefined);
            setShowUpsertModal(false);
            refetch();
          }}
        />
      )}
    </Permission>
  );
};
